import React, { useState, useEffect } from 'react';
import { 
  FiFileText, FiUsers, FiMessageCircle, FiTag, FiEye, FiHeart, 
  FiTrendingUp, FiTrendingDown, FiCalendar, FiClock, FiArrowRight,
  FiBarChart2, FiPieChart, FiActivity, FiUserPlus, FiBookOpen,
  FiStar, FiThumbsUp, FiShare2, FiLoader
} from 'react-icons/fi';
import { dashboardService } from '../../services/dashboardService';
import '../css/Dashboard.css';

function Dashboard() {
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    recentPosts: [],
    recentComments: [],
    topPosts: [],
    popularCategories: [],
    monthlyData: [],
    recentUsers: [],
    recentContacts: [],
    recentEnrollments: [],
    loading: true,
    error: null
  });

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboardService.getDashboardStats();
      // The response structure: { success: true, data: { ... } }
      const apiData = response.data || response;
      
      setDashboardData({
        stats: apiData.stats || {},
        recentPosts: apiData.recentPosts || [],
        recentComments: apiData.recentComments || [],
        topPosts: apiData.topPosts || [],
        popularCategories: apiData.popularCategories || [],
        monthlyData: apiData.monthlyData || [],
        recentUsers: apiData.recentUsers || [],
        recentContacts: apiData.recentContacts || [],
        recentEnrollments: apiData.recentEnrollments || [],
        loading: false,
        error: null
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to load dashboard data'
      }));
    }
  };

  // Generate random but consistent color based on category name
  const getCategoryColor = (categoryName) => {
    const colors = [
      '#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
      '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
    ];
    if (!categoryName) return colors[0];
    const index = Math.abs(categoryName.length * categoryName.charCodeAt(0) % colors.length);
    return colors[index];
  };

  // Generate avatar color based on username
  const getAvatarColor = (username) => {
    const colors = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
    if (!username) return colors[0];
    const index = Math.abs(username.length * username.charCodeAt(0) % colors.length);
    return colors[index];
  };

  // Format trend percentage (positive or negative)
  const formatTrend = (trend) => {
    if (!trend && trend !== 0) return '+0%';
    const num = typeof trend === 'number' ? trend : parseFloat(trend);
    if (isNaN(num)) return '+0%';
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
  };

  if (dashboardData.loading) {
    return (
      <div className="dashboard-loader">
        <div className="dashboard-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (dashboardData.error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-error">
          <FiLoader size={48} />
          <h3>Error Loading Dashboard</h3>
          <p>{dashboardData.error}</p>
          <button onClick={fetchDashboardData} className="retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  // Stats data with real trends from API
  const stats = [
    { 
      title: 'Total Posts', 
      value: dashboardData.stats?.totalPosts || 0,
      icon: <FiFileText />, 
      color: '#4f46e5',
      bg: '#e0e7ff',
      trend: formatTrend(dashboardData.stats?.postsTrend),
      trendUp: (dashboardData.stats?.postsTrend || 0) >= 0
    },
    { 
      title: 'Total Users', 
      value: dashboardData.stats?.totalUsers || 0,
      icon: <FiUsers />, 
      color: '#10b981',
      bg: '#d1fae5',
      trend: formatTrend(dashboardData.stats?.usersTrend),
      trendUp: (dashboardData.stats?.usersTrend || 0) >= 0
    },
    { 
      title: 'Total Views', 
      value: dashboardData.stats?.totalViews || 0,
      icon: <FiEye />, 
      color: '#f59e0b',
      bg: '#fed7aa',
      trend: formatTrend(dashboardData.stats?.viewsTrend),
      trendUp: (dashboardData.stats?.viewsTrend || 0) >= 0
    },
    { 
      title: 'Comments', 
      value: dashboardData.stats?.totalComments || 0,
      icon: <FiMessageCircle />, 
      color: '#8b5cf6',
      bg: '#ede9fe',
      trend: formatTrend(dashboardData.stats?.commentsTrend),
      trendUp: (dashboardData.stats?.commentsTrend || 0) >= 0
    },
    { 
      title: 'Categories', 
      value: dashboardData.stats?.totalCategories || 0,
      icon: <FiTag />, 
      color: '#ef4444',
      bg: '#fee2e2',
      trend: formatTrend(dashboardData.stats?.categoriesTrend),
      trendUp: (dashboardData.stats?.categoriesTrend || 0) >= 0
    },
    { 
      title: 'Enrollments', 
      value: dashboardData.stats?.totalEnrollments || 0,
      icon: <FiUsers />, 
      color: '#ec4899',
      bg: '#fce7f3',
      trend: formatTrend(dashboardData.stats?.enrollmentsTrend),
      trendUp: (dashboardData.stats?.enrollmentsTrend || 0) >= 0
    }
  ];

  // Calculate max views for chart scaling
  const maxMonthlyViews = dashboardData.monthlyData?.length > 0 
    ? Math.max(...dashboardData.monthlyData.map(d => d.views || 0))
    : 1;

  return (
    <div className="dashboard-container">
      <div className="dashboard-wrapper">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">
              <FiActivity className="title-icon" />
              Dashboard Overview
            </h1>
            <p className="dashboard-subtitle">
              Welcome back! Here's what's happening with your site.
            </p>
          </div>
          <div className="header-right">
            <div className="date-badge">
              <FiCalendar />
              <span>{new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card" style={{ borderLeftColor: stat.color }}>
              <div className="stat-card-inner">
                <div className="stat-icon" style={{ background: stat.bg, color: stat.color }}>
                  {stat.icon}
                </div>
                <div className="stat-info">
                  <h3 className="stat-value">{stat.value.toLocaleString()}</h3>
                  <p className="stat-title">{stat.title}</p>
                  <div className={`stat-trend ${stat.trendUp ? 'trend-up' : 'trend-down'}`}>
                    {stat.trendUp ? <FiTrendingUp /> : <FiTrendingDown />}
                    <span>{stat.trend}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="charts-row">
          {/* Monthly Views Chart */}
          <div className="chart-card">
            <div className="card-header-custom">
              <div>
                <h3>
                  <FiBarChart2 />
                  Content Performance
                </h3>
                <p>Monthly views and engagement</p>
              </div>
            </div>
            <div className="chart-body">
              {dashboardData.monthlyData?.length > 0 ? (
                <div className="bar-chart">
                  {dashboardData.monthlyData.map((item, idx) => (
                    <div key={idx} className="bar-item">
                      <div className="bar-label">{item.month}</div>
                      <div className="bar-container">
                        <div 
                          className="bar-fill"
                          style={{ 
                            height: `${((item.views || 0) / maxMonthlyViews) * 100}%` 
                          }}
                        >
                          <span className="bar-tooltip">{item.views?.toLocaleString() || 0}</span>
                        </div>
                      </div>
                      <div className="bar-value">{item.views?.toLocaleString() || 0}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-chart">No data available</div>
              )}
            </div>
          </div>

          {/* Popular Categories */}
          <div className="chart-card">
            <div className="card-header-custom">
              <h3>
                <FiPieChart />
                Popular Categories
              </h3>
            </div>
            <div className="category-list">
              {dashboardData.popularCategories?.length > 0 ? (
                dashboardData.popularCategories.map((category, idx) => {
                  const categoryColor = category.color || getCategoryColor(category.name);
                  const percentage = category.percentage || (category.postCount / dashboardData.stats?.totalPosts) * 100;
                  return (
                    <div key={idx} className="category-item">
                      <div className="category-header">
                        <span className="category-name">{category.name}</span>
                        <span className="category-count">{category.postCount} posts</span>
                      </div>
                      <div className="category-progress">
                        <div 
                          className="progress-fill"
                          style={{ 
                            width: `${percentage}%`,
                            background: `linear-gradient(90deg, ${categoryColor} 0%, ${categoryColor}80 100%)`
                          }}
                        />
                      </div>
                      <div className="category-percent">
                        {Math.round(percentage)}%
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="empty-data">No categories found</div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity Row */}
        <div className="activity-row">
          {/* Top Posts */}
          <div className="activity-card">
            <div className="card-header-custom">
              <h3>
                <FiStar />
                Top Performing Posts
              </h3>
              <span className="badge-custom">Most viewed</span>
            </div>
            <div className="top-posts-list">
              {dashboardData.topPosts?.length > 0 ? (
                dashboardData.topPosts.map((post, idx) => (
                  <div key={idx} className="top-post-item">
                    <div className="post-rank">#{idx + 1}</div>
                    <div className="post-info">
                      <div className="post-title">{post.title}</div>
                      <div className="post-stats">
                        <span><FiEye /> {post.viewCount?.toLocaleString() || 0} views</span>
                        <span><FiHeart /> {post.likeCount || post.likes || 0} likes</span>
                        <span><FiMessageCircle /> {post.commentCount || 0} comments</span>
                      </div>
                    </div>
                    <div className="post-trend">
                      <FiTrendingUp className="trend-icon" />
                      <span>+{post.trendPercentage || post.trend || 12}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-data">No posts found</div>
              )}
            </div>
          </div>

          {/* Recent Comments */}
          <div className="activity-card">
            <div className="card-header-custom">
              <h3>
                <FiMessageCircle />
                Recent Comments
              </h3>
            </div>
            <div className="comments-list">
              {dashboardData.recentComments?.length > 0 ? (
                dashboardData.recentComments.map((comment, idx) => (
                  <div key={idx} className="comment-item">
                    <div className="comment-avatar" style={{ background: getAvatarColor(comment.userName) }}>
                      {comment.userName?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="comment-content">
                      <div className="comment-header">
                        <span className="comment-user">{comment.userName}</span>
                        <span className="comment-time">
                          <FiClock />
                          {new Date(comment.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="comment-text">{comment.content}</div>
                      <div className="comment-post">
                        on: {comment.postTitle}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-data">No comments yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="bottom-row">
          {/* Recent Posts */}
          <div className="recent-card">
            <div className="card-header-custom">
              <h3>
                <FiBookOpen />
                Recent Posts
              </h3>
            </div>
            <div className="recent-posts-table">
              {dashboardData.recentPosts?.length > 0 ? (
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Views</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.recentPosts.map((post, idx) => (
                      <tr key={idx}>
                        <td className="post-title-cell">{post.title}</td>
                        <td>
                          <span 
                            className="category-tag"
                            style={{ background: getCategoryColor(post.categoryName) + '20', color: getCategoryColor(post.categoryName) }}
                          >
                            {post.categoryName || 'Uncategorized'}
                          </span>
                        </td>
                        <td>{post.viewCount?.toLocaleString() || 0}</td>
                        <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="empty-data">No posts found</div>
              )}
            </div>
          </div>

          {/* Recent Users */}
          <div className="recent-card">
            <div className="card-header-custom">
              <h3>
                <FiUserPlus />
                Recent Users
              </h3>
            </div>
            <div className="recent-users-list">
              {dashboardData.recentUsers?.length > 0 ? (
                dashboardData.recentUsers.map((user, idx) => (
                  <div key={idx} className="user-item">
                    <div className="user-avatar" style={{ background: user.avatarColor || getAvatarColor(user.userName) }}>
                      {user.userName?.charAt(0).toUpperCase() || user.fullName?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{user.userName || user.fullName}</div>
                      <div className="user-email">{user.email}</div>
                    </div>
                    <div className="user-join-date">
                      <FiCalendar size={12} />
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-data">No users found</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;